import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import GenericModal from 'src/components/Molecules/Modal';
import { formatCurrency } from 'src/lib/util/currency-utils';
import * as Yup from 'yup';

const PaymentRegistrationModal = ({ invoice, onClose, onSuccess, type }) => {
  const { t } = useTranslation('admin', { keyPrefix: 'payments' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isOverdue = type === 'overdue';
  const items = JSON.parse(invoice.items || '[]');
  const concept = items?.map(item => item.name).join(', ') || 'N/A';
  const originalAmount = items.reduce((sum, item) => sum + item.amount, 0);
  const penaltyAmount = invoice.penaltyApplied ? invoice.amountDue - originalAmount : 0;

  const validationSchema = Yup.object({
    voucher: Yup.mixed().required(t('validation.voucherRequired'))
  });

  const formik = useFormik({
    initialValues: {
      voucher: null
    },
    onSubmit: async values => {
      setLoading(true);
      setError(null);

      try {
        if (!values.voucher) {
          setError('Falta seleccionar un comprobante de pago');
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('invoiceId', invoice.invoiceId);
        formData.append('amount', invoice.amountDue);
        formData.append('method', 'ca');
        // formData.append('comments', `Pago de recibo${isOverdue ? ' vencido' : ''} - ${invoice.concept || 'Concepto'}`);
        formData.append('voucher', values.voucher);

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/payments/manual/register`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('Payment registered successfully:', response.data);
        setSuccess(true);
        if (onSuccess) onSuccess();
      } catch (err) {
        setError(err.response?.data?.message || t('error.general'));
      } finally {
        setLoading(false);
      }
    },
    validationSchema
  });

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        setError('Formato de archivo no permitido. Use JPG, PNG, JPEG, Word o PDF');
        return;
      }

      formik.setFieldValue('voucher', file);
      setError(null);
    }
  };

  if (success) {
    return (
      <GenericModal handleClose={onClose} labels={{ title: t('successTitle') }} minWidth={500} open={true}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('successMessage')}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onClose} variant="contained">
            {t('close')}
          </Button>
        </Box>
      </GenericModal>
    );
  }

  return (
    <GenericModal handleClose={onClose} labels={{ title: t('registerPayment') }} minWidth={500} open={true}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('saveError')}
        </Alert>
      )}

      <Box component="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Concepto */}
          <Grid item xs={12}>
            <TextField disabled fullWidth label={t('columns.concept')} value={concept} variant="outlined" />
          </Grid>

          {/* Monto a pagar */}
          <Grid item md={isOverdue ? 4 : 6} xs={12}>
            <TextField disabled fullWidth label={t('columns.amount')} value={formatCurrency(originalAmount)} variant="outlined" />
          </Grid>

          {/* Monto de penalización (solo para vencidas) */}
          {isOverdue && (
            <Grid item md={4} xs={12}>
              <TextField disabled fullWidth label={t('columns.penalty')} value={formatCurrency(penaltyAmount)} variant="outlined" />
            </Grid>
          )}

          {/* Monto final a pagar */}
          <Grid item md={isOverdue ? 4 : 6} xs={12}>
            <TextField
              disabled
              fullWidth
              label={t('columns.total')}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }
              }}
              value={formatCurrency(invoice.amountDue)}
              variant="outlined"
            />
          </Grid>

          {/* Comprobante */}
          <Grid item xs={12}>
            <Box sx={{ border: '2px dashed #ddd', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <Typography gutterBottom variant="subtitle1">
                {t('voucherLabel')}
              </Typography>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {t('voucherFormats')}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Button component="label" disabled={loading} sx={{ mb: 2 }} variant="outlined">
                  {t('selectFile')}
                  <input accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" hidden onChange={handleFileChange} type="file" />
                </Button>
              </Box>

              {formik.touched.voucher && formik.errors.voucher && (
                <Typography color="error" sx={{ mt: 1 }} variant="caption">
                  {formik.errors.voucher}
                </Typography>
              )}

              {formik.values.voucher && (
                <Box sx={{ bgcolor: 'success.light', borderRadius: 1, mt: 2, p: 2 }}>
                  <Typography color="success.dark" variant="body2">
                    {t('fileSelected')} {formik.values.voucher.name}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {t('fileSize')} {(formik.values.voucher.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              )}

              {!formik.values.voucher && (
                <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                  {t('noFileSelected')}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Información adicional */}
      <Box
        sx={{
          bgcolor: theme => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50]),
          borderRadius: 1,
          mt: 3,
          p: 2
        }}
      >
        <Typography color="text.secondary" variant="body2">
          <strong>{t('invoiceInfo')}</strong>
          <br />
          {t('invoiceId')} {invoice.invoiceId}
          <br />
          {invoice.dueDate && (
            <>
              {t('dueDate')} {new Date(invoice.dueDate).toLocaleDateString()}
              <br />
            </>
          )}
          {t('status')} {invoice.paid ? t('paid') : t('pending')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2 }}>
        <Button disabled={loading} onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
        <Button
          disabled={loading || !formik.values.voucher}
          onClick={formik.handleSubmit}
          startIcon={loading && <CircularProgress size={20} />}
          variant="contained"
        >
          {loading ? t('saving') : t('save')}
        </Button>
      </Box>
    </GenericModal>
  );
};

export default PaymentRegistrationModal;
